import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, Card, Chip, Grid, IconButton, Stack, Step, StepConnector, Stepper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTranslation } from 'react-i18next';
import { renderPriorBox } from './referralsCommon';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import CheckIcon from '@mui/icons-material/Check';
import moment from 'moment';
import config from '../../../../app/config';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {
    getStyles,
    getStylesHistoryIcon,
    getStylesStep,
    getStylesTimeline,
    getStylesTimelineSetpIcon,
    useAddRefDialog,
} from './referralStyles';
import clsx from 'clsx';
import MessageBox from '../../../../util/messageBox';
import SendIcon from '@mui/icons-material/Send';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import { isEmpty } from 'lodash';

const defaultSteps = [
    { key: 'Requested', label: 'REQUESTED' },
    { key: 'Acknowledged', label: 'ACKNOWLEDGED' },
    { key: 'Assessed', label: 'ASSESSED' },
    { key: 'Completed', label: 'COMPLETED' },
];

const getNextStatus = (status) => {
    const currentIndex = defaultSteps.findIndex((step) => step.key === status);
    if (currentIndex === -1) return null; // brak statusu w liście
    return defaultSteps[currentIndex + 1]?.key ?? null; // null jeśli ostatni
};

const getPrevStatus = (status) => {
    const currentIndex = defaultSteps.findIndex((step) => step.key === status);
    if (currentIndex === -1) return null; // brak statusu w liście
    return defaultSteps[currentIndex - 1]?.key ?? null; // null jeśli pierwszy
};

const ReferralStepIcon = ({ active, completed, theme, step }) => {
    const highlighted = active || completed;

    return (
        <Box sx={getStylesTimelineSetpIcon(theme, highlighted, step)}>
            {highlighted ? <CheckIcon sx={{ fontSize: 16 }} color={'#fff'} /> : <AccessTimeRoundedIcon sx={{ fontSize: 14 }} />}
        </Box>
    );
};

const HistoryItem = ({ item, last = false, theme, historyNote }) => {
    const isStatusChange = item.type === 'addstatus' || item.type === 'removestatus';
    const Icon = isStatusChange ? LocalOfferOutlinedIcon : ModeCommentOutlinedIcon;
    const styles = getStylesHistoryIcon(theme, isStatusChange, item.status);
    return (
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={styles.box1}>
                <Box sx={styles.box2}>
                    <Icon sx={{ fontSize: 12 }} />
                </Box>

                {!last && <Box sx={styles.box3} />}
            </Box>

            <Card elevation={0} sx={styles.card}>
                {isStatusChange && <Typography sx={styles.status}>{item.status?.toUpperCase()}</Typography>}
                <Typography sx={styles.note}>{historyNote(item)}</Typography>

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Typography sx={styles.summary.time}>{moment(item.timestamp).format(config.defaultDateTimeFormat)}</Typography>

                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <PersonOutlineRoundedIcon sx={styles.summary.userIcon} />
                        <Typography sx={styles.summary.user}>{item.user}</Typography>
                    </Stack>
                </Stack>
            </Card>
        </Box>
    );
};

const MoveStateDialog = (props) => {
    const { t } = useTranslation();

    const { open, onClose, data, styles, onSave, theme } = props;
    const classes = useAddRefDialog(theme);
    const titlesByStatus = {
        Requested: t('Acknowledge Referral'),
        Acknowledged: t('Record Assesment'),
        Assessed: t('Complete Referral'),
    };
    const [minDate, setMinDate] = useState(moment());
    const [dataForm, setDataForm] = useState({
        timestamp: moment(),
        note: '',
        outcome: '',
    });
    const [isCompleted, setIsCompleted] = useState(false);
    useEffect(() => {
        setDataForm({ timestamp: moment(), note: '' });
        const matchedItem = data?.history
            ?.slice()
            .sort((a, b) => b.id - a.id)
            .find((item) => item.status === data.status);
        const _minDate = matchedItem ? moment(matchedItem.timestamp) : moment(data.RequestedDate);
        setMinDate(_minDate);
        setDataForm(_minDate.isAfter(moment()) ? { timestamp: _minDate, note: '' } : { timestamp: moment(), note: '' });
        setIsCompleted(data.status === 'Assessed');
    }, [data, open]);

    const handleSave = () => {
        onSave(dataForm);
    };

    return (
        <AgyleDialog
            open={open}
            onClose={onClose}
            title={titlesByStatus[data.status]}
            onSave={handleSave}
            submitText={t('Confirm')}
            width="sm"
            fullWidth
            closeButtonOnHeader
            titleStyle={clsx(classes.backgroundOverride, classes.title)}
            actionStyle={classes.backgroundOverride}
            contentStyle={clsx(classes.backgroundOverride, classes.actionNoDivider, classes.maxHeight)}
            noBackDropClose
            disableSubmit={isCompleted ? isEmpty(dataForm.outcome) : false}
        >
            <Grid container spacing={2.5}>
                <Grid item xs={12}>
                    <AgyleDatePicker
                        type="dateTime"
                        inputFormat={config.defaultDateTimeFormat}
                        label={t('Date & Time')}
                        error={!dataForm.timestamp}
                        value={dataForm.timestamp}
                        onChange={(event) => {
                            setDataForm((prevState) => ({ ...prevState, timestamp: event }));
                        }}
                        minDate={minDate}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
                {isCompleted && (
                    <Grid item xs={12}>
                        <AgyleSelectField
                            label={t('Outcome *')}
                            onChange={(val) => setDataForm((prevState) => ({ ...prevState, outcome: val }))}
                            options={[
                                { id: 'DTA', name: t('DTA - Decision to Admit,') },
                                { id: 'NDTA', name: t('NDTA - No Decision to Admit') },
                                { id: 'Follow‑up', name: t('Follow‑up - Follow‑up Required') },
                                { id: 'Discharged', name: t('Discharged - Discharged') },
                            ]}
                            value={dataForm.outcome}
                            noBorder={false}
                            withNewBackground
                            fullWidth
                            clearButtonVisible={false}
                            error={isEmpty(dataForm.outcome)}
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Typography sx={styles.label}>{t('Note (Optional)')}</Typography>
                    <AgyleTextField
                        fullWidth
                        name="note"
                        maxRows={25}
                        minRows={2}
                        debounce
                        multiline
                        value={dataForm.note}
                        onChange={(event) => setDataForm((prevState) => ({ ...prevState, note: event.target.value }))}
                        variant="outlined"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
            </Grid>
        </AgyleDialog>
    );
};

const ReferralsTimeline = (props) => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { t } = useTranslation();
    const [nextStep, setNextStep] = React.useState('Acknowledged');
    const [activeStep, setActiveStep] = React.useState(0);
    const { steps = defaultSteps, onClose, onChange, onDelete, item, userObject } = props;
    const [data, setData] = useState(item);
    const [moveStateDialogOpen, setMoveStateDialogOpen] = useState(false);
    const [moveBackDialogOpen, setMoveBackDialogOpen] = useState(false);
    const [addNote, setAddNote] = useState(false);
    const [newNote, setNewNote] = useState('');
    const doAddNote = () => {
        const lastItemIdx = data.history?.length > 0 ? data.history[data.history.length - 1].id : 0;
        const newItems = [
            {
                id: lastItemIdx + 1,
                timestamp: moment().toISOString(),
                user: userObject.userName,
                type: 'addnote',
                note: newNote,
            },
        ];
        const _data = { ...data, history: [...data.history, ...newItems] };
        onChange(_data);
        setData(_data);
        setAddNote(false);
    };
    const doMoveBack = () => {
        const lastItemIdx = data.history?.length > 0 ? data.history[data.history.length - 1].id : 0;
        const prevStatus = getPrevStatus(data.status);
        const matchedItem = data?.history
            ?.slice()
            .sort((a, b) => b.id - a.id)
            .find((item) => item.status === data.status && item.canceled !== true);
        const updatedHistory = (data.history || []).map((item) => (item.id === matchedItem?.id ? { ...item, canceled: true } : item));

        const newItems = [
            {
                id: lastItemIdx + 1,
                timestamp: moment().toISOString(),
                user: userObject.userName,
                type: 'removestatus',
                status: prevStatus,
            },
        ];
        const _data = { ...data, status: prevStatus, outcome: undefined, history: [...updatedHistory, ...newItems] };
        onChange(_data);
        setData(_data);
        setMoveBackDialogOpen(false);
    };

    const historyNote = (item) => {
        if (item.type === 'removestatus') {
            const prevStatus = getNextStatus(item.status);
            return t(`State reverted from {{prevStatus}} to {{status}}`, {
                status: item.status,
                prevStatus: prevStatus,
            });
        }
        if (item.type === 'addstatus') {
            switch (item.status) {
                case 'Requested':
                    return t('Referral made to {{specialty}}. Reason: {{reason}}', {
                        specialty: data.ReferredToService_SnomedCt?.__description,
                        reason: data.Reason,
                    });
                case 'Acknowledged':
                    return t('Referral Acknowledged');
                case 'Assessed':
                    return t('Referral Assessed');
                case 'Completed':
                    return t('Referral Completed - {{outcome}}', { outcome: item.outcome });
            }
        }
        return item.note;
    };

    const doMoveState = (moveData) => {
        const lastItemIdx = data.history?.length > 0 ? data.history[data.history.length - 1].id : 0;
        const nextStatus = getNextStatus(data.status);
        let newItems = [
            {
                id: lastItemIdx + 1,
                timestamp: moveData.timestamp.toISOString(),
                user: userObject.userName,
                type: 'addstatus',
                status: nextStatus,
                outcome: nextStatus === 'Completed' ? moveData.outcome : undefined,
            },
        ];
        if (moveData.note?.length > 0) {
            newItems.push({
                id: lastItemIdx + 2,
                timestamp: moveData.timestamp.toISOString(),
                user: userObject.userName,
                type: 'addnote',
                note: moveData.note,
            });
        }
        const _data = {
            ...data,
            status: nextStatus,
            outcome: nextStatus === 'Completed' ? moveData.outcome : undefined,
            history: [...data.history, ...newItems],
        };
        onChange(_data);
        setData(_data);
        setMoveStateDialogOpen(false);
    };
    useEffect(() => {
        setNewNote('');
    }, [addNote]);
    useEffect(() => {
        setAddNote(false);
    }, [moveStateDialogOpen, moveBackDialogOpen]);
    useEffect(() => {
        setData(item);
    }, [item]);
    useEffect(() => {
        const status = data?.status;
        if (!status) {
            setActiveStep(0);
        } else {
            const stepIndex = steps.findIndex((step) => step.key === status);
            setActiveStep(stepIndex >= 0 ? stepIndex : 0);
        }
    }, [data]);
    useEffect(() => {
        const next = steps[activeStep + 1]?.key ?? '';
        setNextStep(next);
    }, [activeStep]);

    const getStepDate = (step) => {
        if (step.key === 'Requested') {
            return moment(data.RequestedDate).format(config.defaultDateTimeFormat);
        }
        const matchedItem = data?.history
            ?.slice()
            .sort((a, b) => b.id - a.id)
            .find((item) => item.status === step.key && item.canceled !== true);

        if (matchedItem) {
            return moment(matchedItem.timestamp).format(config.defaultDateTimeFormat);
        }
        return t('Pending');
    };

    const handleStepClick = (index, activeStep) => {
        if (index > activeStep) {
            setMoveStateDialogOpen(true);
        } else {
            setMoveBackDialogOpen(true);
        }
    };
    const tstyles = getStylesTimeline(theme);
    return (
        <>
            <Box sx={tstyles.background}>
                <Card elevation={0} sx={tstyles.card}>
                    <Box sx={tstyles.headerButtonsBackground}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <IconButton size="small" onClick={onClose} sx={tstyles.backIcon}>
                                <ArrowBackRoundedIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            {nextStep?.length > 0 && (
                                <AgyleButton
                                    onClick={() => setMoveStateDialogOpen(true)}
                                    variant="contained"
                                    endIcon={<ChevronRightRoundedIcon />}
                                >
                                    {`Mark as ${nextStep}`}
                                </AgyleButton>
                            )}
                            <IconButton onClick={() => onDelete(data)} sx={tstyles.deleteIcon}>
                                <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Box sx={tstyles.headBackground}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography sx={tstyles.headText}>{t('Specialty Referral')}</Typography>

                            <ChevronRightRoundedIcon sx={tstyles.headIcon} />

                            <Typography sx={tstyles.headSpecialty}>{data.ReferredToService_SnomedCt?.__description}</Typography>

                            {renderPriorBox(data, theme, true)}
                        </Stack>
                    </Box>

                    <Box sx={tstyles.stepBackground}>
                        <Stepper
                            nonLinear
                            activeStep={activeStep}
                            connector={<StepConnector sx={tstyles.stepConnector} />}
                            sx={tstyles.stepper}
                        >
                            {steps.map((step, index) => {
                                const completed = typeof step.completed === 'boolean' ? step.completed : index < activeStep;
                                const isActive = index === activeStep;
                                const canClick = (index > activeStep || index === activeStep) && index > 0;
                                const sstyle = getStylesStep(theme, isActive, completed, canClick, step.key);
                                return (
                                    <Step key={step.key || step.label} completed={completed} sx={sstyle.root}>
                                        <ButtonBase
                                            onClick={() => {
                                                if (canClick) {
                                                    handleStepClick(index, activeStep);
                                                }
                                            }}
                                            disabled={!canClick}
                                            sx={sstyle.button}
                                        >
                                            <Box sx={sstyle.icon}>
                                                <ReferralStepIcon
                                                    active={isActive}
                                                    completed={completed}
                                                    icon={index + 1}
                                                    theme={theme}
                                                    step={step}
                                                />
                                            </Box>

                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={sstyle.label}>{step.label}</Typography>

                                                <Typography sx={sstyle.dateLabel}>{getStepDate(step)}</Typography>
                                            </Box>
                                            {step.key === 'Completed' && data.status === 'Completed' && (
                                                <Chip label={data.outcome || 'unknown'} size="small" sx={sstyle.outcome} />
                                            )}
                                        </ButtonBase>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Box>

                    <Box sx={tstyles.reasonBackground}>
                        <Typography sx={tstyles.reasonText}>{t('REASON')}</Typography>

                        <Typography sx={tstyles.reason}>{data.Reason}</Typography>
                    </Box>

                    <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
                        <Box sx={tstyles.historyBackground}>
                            <Typography sx={tstyles.historyLabel}>{t('REFERRAL HISTORY')}</Typography>

                            <Button startIcon={<NoteAddOutlinedIcon />} onClick={() => setAddNote(true)} sx={tstyles.addNoteButton}>
                                {t('Add Note')}
                            </Button>
                        </Box>
                        {addNote && (
                            <>
                                <Box sx={tstyles.addNoteBackground}>
                                    <AgyleTextField
                                        fullWidth
                                        name="note"
                                        maxRows={25}
                                        minRows={2}
                                        multiline
                                        value={newNote}
                                        onChange={(event) => setNewNote(event.target.value)}
                                        variant="outlined"
                                    />
                                    <Box sx={tstyles.addNoteFoot}>
                                        <AgyleButton onClick={() => setAddNote(false)}>{t('Cancel')}</AgyleButton>
                                        <AgyleButton
                                            onClick={() => doAddNote()}
                                            variant="contained"
                                            startIcon={<SendIcon />}
                                            disabled={newNote.length === 0}
                                        >
                                            {t('Save Note')}
                                        </AgyleButton>
                                    </Box>
                                </Box>
                            </>
                        )}
                        <Stack spacing={2.25}>
                            {data?.history
                                ?.slice()
                                .sort((a, b) => b.id - a.id)
                                .map((item, index, arr) => (
                                    <HistoryItem
                                        key={item.id ?? `${item.type}-${index}`}
                                        item={item}
                                        last={index === arr.length - 1}
                                        theme={theme}
                                        historyNote={historyNote}
                                    />
                                ))}
                        </Stack>
                    </Box>
                </Card>
            </Box>
            <MoveStateDialog
                open={moveStateDialogOpen}
                onClose={() => setMoveStateDialogOpen(false)}
                data={data}
                styles={styles}
                onSave={doMoveState}
                theme={theme}
            />
            <MessageBox
                open={moveBackDialogOpen}
                onClose={() => setMoveBackDialogOpen(false)}
                title={t('Undo Stage')}
                message={`<div>
                    <div>This will revert the referral from <b>${data.status}</b> back to <b>${getPrevStatus(data.status)}</b>.</div>
                    <div style="margin-top: 8px;">The timestamp and any recorded data for this stage will be cleared.</div>
                </div>`}
                haveAccept={{ yes: t('Confirm Undo'), no: t('Cancel') }}
                onAccept={() => doMoveBack()}
                warning
            />
        </>
    );
};

export default ReferralsTimeline;
